// @ts-nocheck
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import translate from "@insite/client-framework/Translate";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import { CheckboxProps } from "@insite/mobius/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable";
import { FormFieldComponentProps } from "@insite/mobius/FormField";
import { IconPresentationProps } from "@insite/mobius/Icon";
import { Button, Modal } from "@insite/mobius/index";
import { ModalPresentationProps } from "@insite/mobius/Modal";
import { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { MobiusStyledComponentPropsWithRef } from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { SignatureData, SignatureModel } from "@store/Framework/Store/Bunzl/Pages/Signature/SignatureReducer";
import EditableModalDisplay from "@store/Library/Components/Signature/EditableModalDisplay";
import { Title } from "@store/Library/Components/Signature/SharedSignatureModalStyles";
import SignatureReceiptDisplay from "@store/Library/Components/Signature/SignatureReceiptDisplay";
import breakpointValues from "@toolkit/Framework/Common/Utilities/breakpointValues";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import SignatureCanvas from "react-signature-canvas";
import React, { useEffect, useRef } from "react";
import styled, { css } from "styled-components";
import { ButtonProps } from "@store/MobiusOverrides/Button/Props";

// isn't exported in optimizely component.  Could change that instead of redefining here.
type TextFieldComponentProps = MobiusStyledComponentPropsWithRef<
    "input",
    { ref?: React.Ref<HTMLInputElement> },
    {
        /** The props for icon displaying on the text input field. */
        iconProps?: IconPresentationProps;
        /** Props to be passed into the Clickable component that will optionally wrap the icon.
         * If `clickableProps` is passed, the icon will be wrapped in a Clickable. If not, the icon will not be clickable. */
        iconClickableProps?: ClickableProps;
        /** Visually hidden text to be rendered within the text field clickable. */
        clickableText?: string;
        /** Error message to be displayed below the input. */
        error?: React.ReactNode;
        /** Hint text to be displayed below the input. */
        hint?: React.ReactNode;
        /** Label to be displayed above the input. */
        label?: React.ReactNode;
    } & Partial<FormFieldComponentProps>
>;

const StyledModal = styled(Modal)`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    padding: 1rem;
    & div[role="dialog"] {
        border-top: 24px solid ${getColor("primary.main")};
        border-radius: ${pxToRem(8)};
        padding: 0rem;
    }
    & div[role="document"] {
        height: 100%;
    }
`;
const Container = styled.div`
    ${breakpointValues(["100%", "80%"], "width")}

    display: flex;

    flex-direction: column;
    align-items: center;
    height: 100%;
    justify-content: space-evenly;
    row-gap: 2rem;
    ${breakpointValues([pxToRem(32), null, pxToRem(24)], "row-gap")}
`;

interface SigningModalProps {
    isOpen: boolean;
    hideEmail?: boolean;
    acknowledgeText?: string;
    onClose: () => void;
    signatureData: SignatureModel;
    editable: boolean;
    signingModalOverrides?: SigningModalOverrides;
    setSignatureState: (signatureData: SignatureData) => void;
    editableName?: boolean;
}

export interface SigningModalOverrides {
    modalProps?: ModalPresentationProps;
    signingInputProps?: TextFieldComponentProps;
    signaturePadCardProps?: StyledProp<"div">;
    closeButtonProps?: ButtonProps;
    saveButtonProps?: ButtonProps;
    clearButtonProps?: ButtonProps;
    containerProps?: StyledProp<"div">;
    declarationProps?: StyledProp<"div">;
    mainTitleProps?: StyledProp<React.FC<TypographyProps>>;
    signatureReceiptDisplayProps?: StyledProp<"div">;
    emailMeContainerProps?: StyledProp<"div">;
    checkboxProps?: CheckboxProps;
    emailInputProps?: TextFieldComponentProps;
    buttonRowProps?: StyledProp<"div">;
}
const SigningModal: React.FC<SigningModalProps> = ({
    isOpen,
    onClose,
    signatureData,
    signingModalOverrides,
    setSignatureState,
    editable,
    acknowledgeText,
    hideEmail,
    editableName = true,
}) => {
    const signatureRef = useRef<SignatureCanvas>(null);
    const [emailAddress, setEmailAddress] = React.useState<string>("");
    const [isEmailRequired, setIsEmailRequired] = React.useState<boolean>(!hideEmail && !!signatureData);
    const [customerName, setCustomerName] = React.useState("");
    const [canvasWidth, setCanvasWidth] = React.useState<number>();
    const [canvasHeight, setCanvasHeight] = React.useState<number>();
    const [hasSubmitted, setHasSubmitted] = React.useState<boolean>(false);
    const [signature, setSignature] = React.useState<string | undefined>();
    const [clearModalOpen, setClearModalOpen] = React.useState(false);
    const [isEmpty, setIsEmpty] = React.useState(true);

    useEffect(() => {
        if (signatureData) {
            setCustomerName(signatureData.customerName);
            setEmailAddress(signatureData.customerEmail ?? "");
            setSignature(signatureData.customerSignature);
        }
        if (signatureRef.current) {
            signatureRef.current.clear();
            signatureRef.current.fromDataURL(signatureData?.customerSignature || "");
        }
    }, [signatureData, signatureRef]);

    useEffect(() => {
        if (signature) {
            const currentPadSignatureString = signatureRef.current?.toDataURL();

            if (signature !== currentPadSignatureString) {
                signatureRef.current?.fromDataURL(signature);
            }
        }
    }, [isOpen, signature]);

    useEffect(() => {
        const canvas = signatureRef.current?.getCanvas();
        if (canvas) {
            setCanvasWidth(canvas.offsetWidth);
            setCanvasHeight(canvas.offsetHeight);
        }
    }, [signatureRef]);
    const handleClear = () => {
        setClearModalOpen(false);
        if (signatureData) {
            setSignatureState({
                customerEmail: "",
                customerName: "",
                customerSignature: "",
                timestamp: "",
            });
        }
        signatureRef.current?.clear();
        setIsEmpty(true);
    };
    const handleSave = () => {
        if (isEmailRequired && !validateEmail(emailAddress)) {
            setHasSubmitted(true);
            return;
        }
        const customerSignature = signatureRef.current?.getTrimmedCanvas().toDataURL("image/svg+xml") ?? "";

        const timestamp = new Date().toISOString();
        const email = isEmailRequired ? emailAddress : undefined;
        const signatureData: SignatureData = {
            customerEmail: email,
            customerName,
            customerSignature,
            timestamp,
        };

        setSignatureState(signatureData);
        handleClose();
    };
    const handleClose = () => {
        setSignature(signatureRef.current?.toDataURL() ?? "");
        onClose();
    };
    return (
        <StyledModal
            closeOnScrimClick={false}
            headline={
                <Title variant="h4" className="signature-modal-title" {...signingModalOverrides?.mainTitleProps}>
                    {!editable
                        ? `${translate("Order #")} ${signatureData.orderNumber}`
                        : translate(`Confirmation Of Receipt`)}
                </Title>
            }
            cssOverrides={{
                modalBody: css`
                    height: 100%;
                `,
                modalTitle: css`
                    border-radius: 0.75rem 0.75rem 0 0;
                    padding: 1rem;
                    background-color: ${getColor("common.background")};
                `,
                contentContainer: css`
                    height: 100%;
                `,
                modalContainer: css`
                    height: 100%;
                `,
                modalContent: css`
                    height: 90%;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    padding-inline: 1rem;
                `,
            }}
            sizeVariant="medium"
            handleClose={handleClose}
            isOpen={isOpen}
            {...signingModalOverrides?.modalProps}
        >
            <Container {...signingModalOverrides?.containerProps} className="signature-modal-container">
                {editable && (
                    <EditableModalDisplay
                        editableName={editableName}
                        hideEmail={hideEmail}
                        acknowledgeText={acknowledgeText}
                        canvasHeight={canvasHeight}
                        isEmailRequired={isEmailRequired}
                        setIsEmailRequired={setIsEmailRequired}
                        signatureRef={signatureRef}
                        canvasWidth={canvasWidth}
                        customerName={customerName}
                        setCustomerName={setCustomerName}
                        emailAddress={emailAddress}
                        setEmailAddress={setEmailAddress}
                        hasSubmitted={hasSubmitted}
                        setClearModalOpen={setClearModalOpen}
                        handleSave={handleSave}
                        setIsEmpty={setIsEmpty}
                        isEmpty={isEmpty}
                    />
                )}
                {!editable && (
                    <SignatureReceiptDisplay
                        signatureData={signatureData}
                        onClose={handleClose}
                        signingModalOverrides={signingModalOverrides}
                    />
                )}
                <TwoButtonModal
                    headlineText={translate("Clear Signature")}
                    messageText={translate("Are you sure you want to clear the signature pad?")}
                    modalIsOpen={clearModalOpen}
                    onSubmit={handleClear}
                    onCancel={() => setClearModalOpen(false)}
                    submitButtonText={translate("Yes")}
                    cancelButtonText={translate("No")}
                />
            </Container>
        </StyledModal>
    );
};

export default SigningModal;
